import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const OTPModal = ({ 
  isOpen, 
  onClose, 
  onVerify, 
  onResend, 
  targetEmail,
  target,
  expiresInMinutes = 10,
  primaryColor = '#8548A8',
  isLoading = false,
  isVerifying = false,
  error = '',
  successMessage = '',
  canResend = false,
  isResending = false,
  timeRemaining = 0,
  formatTime = null,
  attemptsRemaining = 3,
  isExpired = false,
  title,
  subtitle,
  showTermsCheckbox = true,
  verificationTarget
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir && i18n.dir() === 'rtl'
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', ''])
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(0)
  const [localError, setLocalError] = useState('')
  const inputRefs = useRef([])
  const contactTarget = targetEmail || target || ''
  const resolvedVerificationTarget = verificationTarget || (contactTarget && contactTarget.includes('@') ? 'email' : 'phone')
  const modalTitle = title || t('otpModal.title')
  const modalSubtitle = subtitle || t('otpModal.subtitle', { target: t(`otpModal.target.${resolvedVerificationTarget}`) })
  const verifyLoading = isLoading || isVerifying

  // Initialize refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6)
  }, [])

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtpCode(['', '', '', '', '', ''])
      setAcceptTerms(false)
      setFocusedIndex(0)
      setLocalError('')
      // Focus first input after a small delay
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus()
        }
      }, 100)
    }
  }, [isOpen])

  // Clear local error when external error changes
  useEffect(() => {
    if (error) {
      setLocalError('')
    }
  }, [error])

  // Auto-focus management
  useEffect(() => {
    if (isOpen && !verifyLoading && inputRefs.current[focusedIndex]) {
      inputRefs.current[focusedIndex].focus()
    }
  }, [focusedIndex, isOpen, verifyLoading])

  // Format time display
  const getFormattedTime = () => {
    if (formatTime) return formatTime(timeRemaining)
    
    const mins = Math.floor(timeRemaining / 60)
    const secs = timeRemaining % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return
    
    if (value.length > 1) return // Prevent multiple characters
    
    const newOtp = [...otpCode]
    newOtp[index] = value
    setOtpCode(newOtp)
    
    // Clear local error when user starts typing
    if (localError) setLocalError('')
    
    // Auto-focus next input
    if (value && index < 5) {
      setFocusedIndex(index + 1)
    }
  }

  // Handle backspace and navigation
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otpCode[index] && index > 0) {
        // Move to previous input if current is empty
        setFocusedIndex(index - 1)
      } else {
        // Clear current input
        const newOtp = [...otpCode]
        newOtp[index] = ''
        setOtpCode(newOtp)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setFocusedIndex(index - 1)
    } else if (e.key === 'ArrowRight' && index < 5) {
      setFocusedIndex(index + 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleVerify()
    }
  }

  // Paste handler
  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    
    if (pastedData.length > 0) {
      const newOtp = pastedData.split('')
      while (newOtp.length < 6) newOtp.push('')
      setOtpCode(newOtp)
      
      // Focus last filled input or first empty one
      const nextIndex = Math.min(pastedData.length, 5)
      setFocusedIndex(nextIndex)
      
      // Clear errors
      if (localError) setLocalError('')
    }
  }

  // Handle verify
  const handleVerify = () => {
    const code = otpCode.join('')
    
    // Validate code length
    if (code.length !== 6) {
      setLocalError(t('otpModal.errors.incompleteCode'))
      return
    }
    
    // Validate terms acceptance if required
    if (showTermsCheckbox && !acceptTerms) {
      setLocalError(t('otpModal.errors.acceptTerms'))
      return
    }
    
    // Clear local error and proceed
    setLocalError('')
    onVerify(code)
  }

  // Handle resend
  const handleResend = async () => {
    setOtpCode(['', '', '', '', '', ''])
    setFocusedIndex(0)
    setLocalError('')
    await onResend()
  }

  // Get masked email/phone for display
  const getMaskedTarget = (target) => {
    if (!target) return ''
    
    if (target.includes('@')) {
      // Email masking
      const [username, domain] = target.split('@')
      if (username.length <= 3) return target
      const maskedUsername = username.substring(0, 3) + '***' + username.slice(-2)
      return `${maskedUsername}@${domain}`
    } else {
      // Phone number masking
      const cleaned = target.replace(/\D/g, '')
      if (cleaned.length <= 6) return target
      return cleaned.substring(0, 3) + '***' + cleaned.slice(-3)
    }
  }

  // Determine button state
  const isVerifyDisabled = verifyLoading || otpCode.join('').length !== 6 || (showTermsCheckbox && !acceptTerms)
  const isResendDisabled = verifyLoading || isResending || !canResend

  // Get status message
  const getStatusMessage = () => {
    if (isExpired || timeRemaining === 0) return t('otpModal.status.expired')
    if (timeRemaining > 0) return t('otpModal.status.timeLeft', { time: getFormattedTime() })
    return t('otpModal.status.expired')
  }

  const getStatusColor = () => {
    if (isExpired || timeRemaining === 0) return '#EF4444'
    if (timeRemaining < 60) return '#F59E0B'
    return '#6B7280'
  }

  // Get current error to display
  const currentError = localError || error

  // Don't render if not open
  if (!isOpen) return null

  const handleBackdropClick = (e) => {
    // Close modal when clicking on backdrop
    if (e.target === e.currentTarget && !isLoading && !isResending) {
      onClose()
    }
  }

  return (
    <div 
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        fontFamily: '"Red Hat Display", sans-serif',
        backdropFilter: 'blur(4px)',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      {/* Enhanced OTP Modal */}
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '500px',
          maxWidth: '90vw',
          background: '#FFFFFF',
          borderRadius: '20px',
          padding: '32px',
          boxSizing: 'border-box',
          position: 'relative',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
          maxHeight: '90vh',
          overflowY: 'auto',
          animation: 'modalSlideIn 0.3s ease-out',
          textAlign: 'center'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={verifyLoading || isResending}
          style={{
            position: 'absolute',
            top: '16px',
            right: isRTL ? 'auto' : '16px',
            left: isRTL ? '16px' : 'auto',
            width: '32px',
            height: '32px',
            border: 'none',
            background: 'transparent',
            cursor: (verifyLoading || isResending) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s ease',
            opacity: (verifyLoading || isResending) ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!(verifyLoading || isResending)) {
              e.target.style.background = '#F3F4F6'
            }
          }}
          onMouseLeave={(e) => {
            if (!(verifyLoading || isResending)) {
              e.target.style.background = 'transparent'
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        
        {/* Modal Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            fontSize: '24px',
            color: '#1F2937',
            margin: '0 0 8px 0'
          }}>
            {modalTitle}
          </h2>
          <p style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            color: '#6B7280',
            margin: 0,
            lineHeight: 1.5
          }}>
            {modalSubtitle}<br/>
            <strong style={{ color: '#374151' }}>
              {getMaskedTarget(contactTarget)}
            </strong>
          </p>
        </div>
        
        {/* Success Message */}
        {successMessage && (
          <div style={{
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#DCFCE7',
              borderRadius: '8px',
              color: '#16A34A',
              fontSize: '14px',
              fontWeight: 500
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {successMessage}
            </div>
          </div>
        )}
        
        {/* OTP Input Fields */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}>
          {otpCode.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              onFocus={() => setFocusedIndex(index)}
              disabled={verifyLoading || isResending}
              style={{
                width: '48px',
                height: '48px',
                border: `2px solid ${
                  currentError && !successMessage ? '#EF4444' : 
                  focusedIndex === index ? primaryColor : 
                  digit ? primaryColor : '#E5E7EB'
                }`,
                borderRadius: '8px',
                textAlign: 'center',
                direction: 'ltr',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '20px',
                fontWeight: 600,
                color: '#1F2937',
                outline: 'none',
                transition: 'all 0.2s ease',
                background: (verifyLoading || isResending) ? '#F9FAFB' : '#FFFFFF',
                cursor: (verifyLoading || isResending) ? 'not-allowed' : 'text'
              }}
            />
          ))}
        </div>
        
        {/* Error Message */}
        {currentError && !successMessage && (
          <div style={{
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '13px',
              color: '#EF4444',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#EF4444"/>
                <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {currentError}
            </p>
            {attemptsRemaining > 0 && attemptsRemaining < 3 && (
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '12px',
                color: '#9CA3AF',
                margin: '4px 0 0 0'
              }}>
            {t('otpModal.attemptsRemaining', { count: attemptsRemaining })}
              </p>
            )}
          </div>
        )}
        
        {/* Terms Checkbox */}
        {showTermsCheckbox && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            justifyContent: 'center'
          }}>
            <input
              type="checkbox"
              id="acceptTermsModal"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              disabled={verifyLoading || isResending}
              style={{
                width: '16px',
                height: '16px',
                cursor: (verifyLoading || isResending) ? 'not-allowed' : 'pointer',
                accentColor: primaryColor
              }}
            />
            <label 
              htmlFor="acceptTermsModal"
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '13px',
                color: '#6B7280',
                cursor: (verifyLoading || isResending) ? 'not-allowed' : 'pointer',
                userSelect: 'none'
              }}
            >
              {t('otpModal.terms.label')}
            </label>
            <span 
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '13px',
                color: primaryColor,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 500
              }}
              onClick={() => window.open('/terms', '_blank')}
            >
              {t('otpModal.links.viewTerms')}
            </span>
          </div>
        )}
        
        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'center'
        }}>
          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={isVerifyDisabled}
            style={{
              width: '100%',
              maxWidth: '200px',
              height: '44px',
              background: isVerifyDisabled ? '#E5E7EB' : primaryColor,
              borderRadius: '8px',
              border: 'none',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              color: isVerifyDisabled ? '#9CA3AF' : '#FFFFFF',
              cursor: isVerifyDisabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!isVerifyDisabled) {
                e.target.style.transform = 'translateY(-1px)'
                e.target.style.boxShadow = `0 4px 12px ${primaryColor}40`
              }
            }}
            onMouseLeave={(e) => {
              if (!isVerifyDisabled) {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = 'none'
              }
            }}
          >
            {verifyLoading && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{
                animation: 'spin 1s linear infinite'
              }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416" style={{
                  animation: 'spin 1s linear infinite'
                }}/>
              </svg>
            )}
            {verifyLoading ? t('otpModal.buttons.verifying') : t('otpModal.buttons.verify')}
          </button>
          
          {/* Timer Display */}
          <div style={{
            textAlign: 'center',
            marginBottom: '8px'
          }}>
            <span style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '18px',
              color: getStatusColor(),
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {getStatusMessage()}
            </span>
          </div>
          
          {/* Resend Button */}
          {canResend || isExpired ? (
            <button
              onClick={handleResend}
              disabled={isResendDisabled}
              style={{
                background: 'transparent',
                border: `1px solid ${isResendDisabled ? '#E5E7EB' : '#6B7280'}`,
                borderRadius: '6px',
                padding: '8px 16px',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: isResendDisabled ? '#9CA3AF' : '#6B7280',
                cursor: isResendDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                if (!isResendDisabled) {
                  e.target.style.background = '#F9FAFB'
                  e.target.style.borderColor = '#4B5563'
                  e.target.style.color = '#4B5563'
                }
              }}
              onMouseLeave={(e) => {
                if (!isResendDisabled) {
                  e.target.style.background = 'transparent'
                  e.target.style.borderColor = '#6B7280'
                  e.target.style.color = '#6B7280'
                }
              }}
            >
              {isResending && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{
                  animation: 'spin 1s linear infinite'
                }}>
                  <path d="M12 2v4M12 18v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
              {isResending ? t('otpModal.buttons.sending') : t('otpModal.buttons.resend')}
            </button>
          ) : (
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '13px',
              color: '#9CA3AF',
              margin: 0,
              textAlign: 'center'
            }}>
              {t('otpModal.support.prompt', { target: t(`otpModal.target.${resolvedVerificationTarget}`) })}{' '}
              <span 
                style={{ 
                  color: primaryColor, 
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 500
                }}
                onClick={() => window.open('/support', '_blank')}
              >
                {t('otpModal.support.contact')}
              </span>
            </p>
          )}
        </div>
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes modalSlideIn {
            0% { 
              opacity: 0; 
              transform: scale(0.9) translateY(-20px); 
            }
            100% { 
              opacity: 1; 
              transform: scale(1) translateY(0); 
            }
          }
        `}
      </style>
    </div>
  )
}

export default OTPModal